import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, errText } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { PawPrint, Loader2, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    if (pw.length < 6) return toast.error("Password must be at least 6 characters");
    if (pw !== confirm) return toast.error("Passwords don't match");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password: pw });
      setDone(true);
      toast.success("Password reset successfully!");
    } catch (e) { toast.error(errText(e)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-16" data-testid="reset-password-page">
      <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
        <div className="flex items-center gap-2 mb-1">
          <span className="h-9 w-9 rounded-xl bg-[#F5551E] flex items-center justify-center"><PawPrint size={20} className="text-white" /></span>
          <h1 className="font-display text-xl font-bold text-[#12234A]">Choose a new password</h1>
        </div>

        {!token ? (
          <p className="mt-6 text-slate-500">This reset link is invalid. Please request a new one from the <Link to="/forgot-password" className="text-[#F5551E] font-medium">forgot password</Link> page.</p>
        ) : done ? (
          <div className="mt-6 text-center" data-testid="reset-done">
            <CheckCircle2 size={44} className="mx-auto text-emerald-500" />
            <p className="mt-3 text-slate-600">Your password has been updated. You can now sign in with your new password.</p>
            <Button onClick={() => nav("/")} className="mt-6 rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white">Go to sign in</Button>
          </div>
        ) : (
          <div className="mt-6 space-y-4">
            <div>
              <Label htmlFor="rp-pw">New password</Label>
              <Input id="rp-pw" type="password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-password" className="mt-1 h-11 rounded-xl" placeholder="••••••••" />
            </div>
            <div>
              <Label htmlFor="rp-confirm">Confirm password</Label>
              <Input id="rp-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm" className="mt-1 h-11 rounded-xl" placeholder="••••••••" />
            </div>
            <Button onClick={submit} disabled={loading} data-testid="reset-submit" className="w-full h-11 rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white font-semibold">
              {loading ? <Loader2 className="animate-spin" size={18} /> : "Reset password"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
